import { Flex, FormControl, Text, useToast } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { ButtonCmp } from '../../components/ButtonCmp'
import { ImgSah } from '../../components/ImgSah'
import { SelectCmp } from '../../components/SelectCmp'
import { TextAreaCmp } from '../../components/TextareaCmp'
import { WhiteContainer } from '../../components/WhiteContainer'
import { appApi } from '../../services/appApi'
import { parseJwt } from '../../services/token'
import { ModalCanalDenuncia } from '../Colaborador/Perfil/Components/ModalCanalDenuncia'
import { Body } from '../Layouts/Body'
import { BoxMensagem } from './Components/BoxMensagem'

interface Denuncia {
  id: string
  empresaId: string
  texto: string
  status: number
  dataCriacao: string
  dataAlterado?: string
  atualizacoes?: Atualizacao[]
}

interface Atualizacao {
  colaboradorId: string
  resposta: string
  avatar: string
  nome: string
  dataCriacao: string
  dataAlterado?: string
}

type iEtapa = 'inicial' | 'formulario' | 'enviado'

const TituloAcolhimento = 'Canal de Ouvidoria | Confidencial e Sigiloso'

const TextoAcolhimento = [
  'Este é um canal confidencial e sigiloso, criado para oferecer um espaço seguro de escuta, manifestação e registro de situações relacionadas ao ambiente de trabalho.',
  'Por meio deste canal, você pode relatar, com segurança, situações que possam afetar sua integridade, bem-estar e relações de trabalho, incluindo assédio moral ou sexual, discriminação, conflitos, violência, condições inadequadas de trabalho e outros fatores relacionados a riscos psicossociais.',
  'As informações registradas serão tratadas com confidencialidade, respeito, sem a identificação e acesso restrito às pessoas responsáveis pela análise e condução do relato, observando os procedimentos internos e a legislação.',
  'Este canal integra as ações da organização para identificação, prevenção e gerenciamento de riscos psicossociais relacionados ao trabalho, em alinhamento às diretrizes de Saúde e Segurança do Trabalho e da NR-1.',
  'Seu relato será tratado com seriedade, imparcialidade, anonimizado e confidencial.',
  'Caso você queira se identificar nesse canal, necessário escrever explicitamente o seu nome no relato.'
]

export const CanalDenuncia: React.FC = () => {
  const toast = useToast()
  const IsAdmin = parseJwt().adm === 'True'

  const [Denuncias, setDenuncias] = useState<Denuncia[]>([])
  const [Status, setStatus] = useState('')
  const [ModalDenunciaIsOpen, setModalDenunciaIsOpen] = useState(false)
  const [resetarModal, setResetarModal] = useState(0)
  const [IsLoading, setIsLoading] = useState(false)

  const [Etapa, setEtapa] = useState<iEtapa>('inicial')
  const [Texto, setTexto] = useState('')
  const [IsLoadingEnvio, setIsLoadingEnvio] = useState(false)

  function getDenuncias(): void {
    setIsLoading(true)
    appApi.get('api/ChamadosOuvidoria')
      .then(res => {
        if (res.status === 200) {
          setDenuncias(res.data ?? [])
        }
      })
      .catch(err => console.log(err))
      .finally(() => setIsLoading(false))
  }

  function PostDenuncia(texto: string): void {
    if (texto.trim() === '') {
      toast({
        title: 'Descreva a situação antes de enviar',
        status: 'error',
        duration: 4000,
        position: 'top'
      })
      return
    }

    const form = {
      texto: texto,
      avatar: ''
    }

    setIsLoadingEnvio(true)
    appApi.post('api/ChamadosOuvidoria', form)
      .then(() => {
        toast({
          title: 'Denúncia enviada com sucesso',
          status: 'success',
          duration: 4000,
          position: 'top'
        })
        if (IsAdmin) {
          setModalDenunciaIsOpen(false)
          setResetarModal(prev => prev + 1)
          getDenuncias()
        } else {
          setTexto('')
          setEtapa('enviado')
        }
      })
      .catch(err => {
        console.log(err)
        toast({
          title: 'Erro ao enviar a denúncia, tente novamente mais tarde',
          status: 'error',
          duration: 4000,
          position: 'top'
        })
      })
      .finally(() => setIsLoadingEnvio(false))
  }

  function CancelarDenuncia(): void {
    setTexto('')
    setEtapa('inicial')
  }

  useEffect(() => {
    if (IsAdmin) {
      getDenuncias()
    }
  }, [])

  // Em andamento (0) antes dos finalizados (1); dentro do grupo mantem a ordem da api
  const DenunciasFiltradas = Denuncias
    .filter(e => Status === '' || e.status === parseInt(Status))
    .sort((a, b) => a.status - b.status)

  if (!IsAdmin) {
    return (
      <Body>
        <WhiteContainer>
          <Flex flexDir={'column'} gap={'1rem'}>
            <h1>Ouvidoria</h1>
            {
              (Etapa !== 'enviado') && (
                <Flex flexDir={'column'} gap={'.5rem'} maxW={'50rem'}>
                  <h3>{TituloAcolhimento}</h3>
                  {
                    TextoAcolhimento.map((e, i) => (
                      <Text key={i} fontSize={'.875rem'} color={'var(--Gray1)'}>{e}</Text>
                    ))
                  }
                </Flex>
              )
            }

            {
              (Etapa === 'inicial') && (
                <Flex>
                  <ButtonCmp
                    onClick={() => setEtapa('formulario')}
                    VarColor='Green2'
                  >Abrir denúncia</ButtonCmp>
                </Flex>
              )
            }

            {
              (Etapa === 'formulario') && (
                <Flex flexDir={'column'} gap={'.75rem'}>
                  <Flex flexDir={'column'}>
                    <h3>Nos conte o que aconteceu</h3>
                    <Text fontSize={'.875rem'} color={'var(--Gray1)'} maxW={'50rem'}>Descreva a situação com o máximo de detalhes possível. Ao enviar a mensagem, os responsáveis pela ouvidoria receberão o registro e darão o encaminhamento necessário.</Text>
                  </Flex>
                  <TextAreaCmp
                    value={Texto}
                    OnChange={(e) => setTexto(e)}
                    height={'10rem'}
                    placeholder='Descreva a situação'
                  />
                  <Flex gap={'.5rem'}>
                    <ButtonCmp onClick={CancelarDenuncia} OutlineColor='Red'>Cancelar</ButtonCmp>
                    <ButtonCmp
                      onClick={() => PostDenuncia(Texto)}
                      isLoading={IsLoadingEnvio}
                      VarColor='Green2'
                    >Enviar denúncia</ButtonCmp>
                  </Flex>
                </Flex>
              )
            }

            {
              (Etapa === 'enviado') && (
                <ImgSah center textoPersonalizado='Sua denúncia foi registrada e será tratada com cuidado e confidencialidade pela Ouvidoria de RH'>
                  <ButtonCmp onClick={() => setEtapa('formulario')} OutlineColor='Green2'>Abrir outra denúncia</ButtonCmp>
                </ImgSah>
              )
            }
          </Flex>
        </WhiteContainer>
      </Body>
    )
  }

  return (
    <Body isLoading={IsLoading}>
      <ModalCanalDenuncia
        key={resetarModal}
        isOpen={ModalDenunciaIsOpen}
        onConfirm={PostDenuncia}
        onRequestClose={() => setModalDenunciaIsOpen(false)}
      />
      <WhiteContainer>
        <Flex justifyContent={'space-between'}>
          <h1>Ouvidoria</h1>
            <ButtonCmp
              onClick={() => setModalDenunciaIsOpen(true)}
              VarColor='Green2'
            >Abrir denúncia</ButtonCmp>
        </Flex>
        <Flex marginTop={'1rem'} gap={'1rem'}>
              <FormControl width={'30%'} >
                <SelectCmp title='Status' OnChange={(e) => setStatus(e)}>
                  <option value={''}>Todos</option>
                  <option value={'0'}>Em andamento</option>
                  <option value={'1'}>Finalizado</option>
                </SelectCmp>
              </FormControl>
        </Flex>
        {
          (Denuncias.length > 0) && (
            <Flex marginTop={'1rem'} flexDir={'column'} gap='1rem'>
              {
                DenunciasFiltradas.map((e) => {
                  return (
                    <BoxMensagem
                      reload={getDenuncias}
                      denuncia={e}
                      key={e.id}
                    />
                  )
                })
              }
            </Flex>
          )
        }

        {
          (Denuncias.length === 0 && !IsLoading) && (
            <ImgSah textoPersonalizado='Ainda não há denúncias registradas' />
          )
        }
      </WhiteContainer>
    </Body>
  )
}
